import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useGameStore } from '../store/gameStore';

const SHOWN_TODAY_KEY = 'cellsnake:rewardPokeShownDate';
const DELAY_MS = 60_000; // 1 minute of play before poking the player

/** Today's ISO date for the per-day shown-flag. */
function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Reward-poke toast — appears once per day, ~1 minute after the player
 * starts playing (i.e. after the game canvas mounts and the player is
 * alive). Reminds them about the daily winner prize so the leaderboard
 * goal feels real, not theoretical.
 *
 * Shows ONCE per calendar day to avoid nagging. Dismissible immediately.
 *
 * Lives in the HUD because the timer should pause when the player isn't
 * actually playing (e.g. tab not focused, dead and looking at the death
 * modal — both pause `alive` or the engine itself). The simplest signal
 * for "playing" is `alive === true` from the HUD state.
 */
export function RewardPokeToast() {
  const alive = useGameStore((s) => s.hud.alive);
  const [shown, setShown] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (shown) return;
    if (!alive) return;
    try {
      if (localStorage.getItem(SHOWN_TODAY_KEY) === todayKey()) {
        setShown(true);
        return;
      }
    } catch { /* private mode — silently continue */ }
    const t = window.setTimeout(() => {
      // Re-check alive at fire-time: if the player died between the
      // start of the timer and now, don't poke them — they'll get the
      // poke on the next life instead.
      if (useGameStore.getState().hud.alive) {
        setVisible(true);
        setShown(true);
        try { localStorage.setItem(SHOWN_TODAY_KEY, todayKey()); } catch { /* noop */ }
      }
    }, DELAY_MS);
    return () => clearTimeout(t);
  }, [alive, shown]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: -24, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -16, scale: 0.97 }}
          transition={{ type: 'spring', stiffness: 380, damping: 28 }}
          className="absolute top-16 left-1/2 -translate-x-1/2 z-20 max-w-[340px] pointer-events-auto"
        >
          <div
            className="rounded-2xl p-4 text-center"
            style={{
              background:
                'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(238,243,255,0.95) 100%)',
              boxShadow: '0 16px 50px rgba(255,210,61,0.45), 0 0 0 1px rgba(255,255,255,0.6) inset',
              color: '#0e1424',
            }}
          >
            <div
              className="text-[9px] uppercase tracking-[0.35em] mb-1"
              style={{ color: '#b8881e' }}
            >
              🏆 keep going
            </div>
            <div className="text-sm font-semibold leading-snug mb-3">
              Play strong on your way to becoming today's leader and win{' '}
              <span style={{ color: '#b8881e' }}>$20</span>!
            </div>
            <button
              onClick={() => setVisible(false)}
              className="px-3 py-1 rounded-md text-[10px] uppercase tracking-[0.22em] bg-slate-900 text-white hover:bg-slate-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
            >
              got it
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
